/*!
  Pioneer WFRIA Footer v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["Footer"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("Footer") < 0) {
    window.__PIONEER_GLOBAL__.components.push("Footer");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["className", "links", "singleCopyrightYear", "noDefaultLinks", "customContent", "extraContent", "customStrings"],
      _excluded2 = ["label", "href", "target"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import PropTypes from "prop-types";
import React from "react";
import Anchor from "../Anchor/Anchor";
import { invalidIf } from "../common/proptypes";
import { FOOTER_NAVIGATION_CLASS } from "../themes/common/classes";
import { COPYRIGHT_FIRST_YEAR, CURRENT_YEAR, DEFAULT_TARGET, DISCLOSURES_LINK } from "./constants";
import extractTheme from "./extractTheme";
import STRINGS from "./strings";
/**
 * Footer is a component located at the bottom of the page.
 * It is used to house additional site links and copyright
 * information. A limited number of links can also be
 * added as required.
 */

import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";

function Footer(_ref) {
  let {
    className,
    links,
    singleCopyrightYear,
    noDefaultLinks,
    customContent,
    extraContent,
    customStrings
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  const theme = useTheme();
  const {
    cssRoot,
    cssBody,
    cssCopyright,
    cssLinks
  } = extractTheme(_objectSpread({
    theme
  }, otherProps));

  const componentStrings = _objectSpread(_objectSpread({}, STRINGS), customStrings);

  const {
    allRightsReserved,
    navAriaLabel,
    privacyLabel
  } = componentStrings;
  const defaultLink = {
    href: DISCLOSURES_LINK,
    target: DEFAULT_TARGET,
    label: privacyLabel
  };
  const linkList = noDefaultLinks ? links : [defaultLink, ...links];
  const hasLinks = linkList.length > 0;
  return _jsx("footer", _objectSpread(_objectSpread({
    className: className,
    role: "contentinfo",
    css: cssRoot
  }, otherProps), {}, {
    children: _jsx("div", {
      css: cssBody,
      children: _jsxs("span", {
        children: [extraContent && _jsxs(React.Fragment, {
          children: [extraContent, _jsx("hr", {})]
        }), customContent ? _jsx("div", {
          children: customContent
        }) : hasLinks && _jsx("nav", {
          "aria-label": navAriaLabel,
          className: FOOTER_NAVIGATION_CLASS,
          children: _jsx("ul", {
            children: linkList.map(_ref2 => {
              let {
                label,
                href,
                target = DEFAULT_TARGET
              } = _ref2,
                  rest = _objectWithoutProperties(_ref2, _excluded2);

              return _jsx("li", {
                css: cssLinks,
                children: _jsx(Anchor, _objectSpread(_objectSpread({
                  kind: "simple",
                  href: href,
                  target: target
                }, rest), {}, {
                  children: label
                }))
              }, label);
            })
          })
        }), singleCopyrightYear ? _jsxs("span", {
          css: cssCopyright,
          "data-testid": "ftr-cprght",
          children: ["\xA9 ", CURRENT_YEAR, " ", allRightsReserved]
        }) : _jsxs("span", {
          css: cssCopyright,
          "data-testid": "ftr-cprght",
          children: ["\xA9 ", COPYRIGHT_FIRST_YEAR, " - ", CURRENT_YEAR, " ", allRightsReserved]
        })]
      })
    })
  }));
}

Footer.propTypes = process.env.NODE_ENV !== "production" ? {
  className: PropTypes.string,

  /**
   * The `customContent` prop replaces the `links` section of the
   * Footer with the provided custom content.
   * When it is used, the `links` and `noDefaultLinks` props
   * will be ignored.
   */
  customContent: PropTypes.node,

  /**
   * Override for strings used within the component.
   */
  customStrings: PropTypes.shape({
    /**
     * The "All rights reserved" text.
     * Defaults to "Wells Fargo. All rights reserved.".
     */
    allRightsReserved: PropTypes.string,

    /**
     * The aria label of the top nav element.
     * Defaults to "Legal information".
     */
    navAriaLabel: PropTypes.string,

    /**
     * The Privacy label.
     * Defaults to "Privacy, Cookies, Security, and Legal".
     */
    privacyLabel: PropTypes.string
  }),

  /**
   * The `extraContent` prop allows you to add an extra content
   * above the default links. This additional content may contain
   * links or copy as required by the application.
   */
  extraContent: PropTypes.node,

  /**
   * An array of objects, each of which contains data about
   * each link.
   */
  links: invalidIf(PropTypes.arrayOf(PropTypes.shape({
    /** The URL to which the anchor element points. */
    href: PropTypes.string.isRequired,

    /** The visible text of the anchor element. */
    label: PropTypes.string.isRequired,

    /** The target window in which the new URL should be opened. */
    target: PropTypes.oneOf(["_blank", "_self", "_parent", "_top"])
  })), (props, propName) => {
    const links = props[propName];
    return Array.isArray(links) && links.length > 0 && props.customContent !== null;
  }, `when you supply "props.customContent".`),

  /**
   * Whether the CEO privacy links will appear in the Footer.
   */
  noDefaultLinks: invalidIf(PropTypes.bool, (props, propName) => props[propName] === true && props.customContent !== null, `when you supply "props.customContent".`),

  /**
   * Whether the copyright date should be a range or a single year.
   */
  singleCopyrightYear: PropTypes.bool
} : {};
Footer.defaultProps = {
  children: null,
  className: null,
  links: [],
  singleCopyrightYear: false,
  noDefaultLinks: false,
  customContent: null,
  extraContent: null,
  customStrings: null
};
export default Footer;